import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, FileDown, ShieldCheck, AlertTriangle, Package, Truck, Building2 } from 'lucide-react';
import { Topbar } from '@/components/layout/Topbar';
import { piecesApi, type StatutPiece } from '@/api/pieces';
import { toast } from '@/stores/toastStore';

/**
 * SIMG-TRACABILITE-PIECES-2026-001 — Écran back-office de suivi des pièces d'un lot.
 *
 * Le DG / manager voit ici l'ensemble des pièces produites pour un lot avec
 * leur statut (sur parc, expédiée, posée, défectueuse). Bouton "Télécharger
 * la feuille A4" pour imprimer les codes au poste.
 *
 * URL : /operations/production/lots/:lotId/pieces
 * Accès : admin_holding, dg_filiale, daf, manager (contrôle back).
 */

const STATUT_LABELS: Record<StatutPiece, { label: string; ton: string; icone: any }> = {
  sur_parc:    { label: 'Sur parc',    ton: 'ds-bd-b', icone: Package },
  expediee:    { label: 'Expédiée',    ton: 'ds-bd-p', icone: Truck },
  posee:       { label: 'Posée',       ton: 'ds-bd-g', icone: Building2 },
  defectueuse: { label: 'Défectueuse', ton: 'ds-bd-r', icone: AlertTriangle },
};

export function PiecesLotPage() {
  const { lotId } = useParams<{ lotId: string }>();
  const [filtreStatut, setFiltreStatut] = useState<StatutPiece | null>(null);
  const [telecharge, setTelecharge]     = useState(false);

  const { data: pieces, isLoading, error } = useQuery({
    queryKey:  ['production', 'lots', lotId, 'pieces'] as const,
    queryFn:   () => piecesApi.listerParLot(lotId!),
    enabled:   !!lotId,
    staleTime: 30_000,
  });

  const compteurs = useMemo(() => {
    const c: Record<StatutPiece, number> = { sur_parc: 0, expediee: 0, posee: 0, defectueuse: 0 };
    (pieces ?? []).forEach((p) => { c[p.statut] = (c[p.statut] ?? 0) + 1; });
    return c;
  }, [pieces]);

  const piecesFiltrees = useMemo(() => {
    if (!pieces) return [];
    return filtreStatut ? pieces.filter((p) => p.statut === filtreStatut) : pieces;
  }, [pieces, filtreStatut]);

  const lotReference = pieces?.[0]
    ? pieces[0]!.codeCanonique.replace(/-\d{4}-[0-9A-Z]{2}$/, '')
    : 'lot';

  const handleTelecharger = async () => {
    setTelecharge(true);
    try {
      await piecesApi.telechargerFeuille(lotId!, lotReference);
      toast.success('Feuille téléchargée', 'PDF enregistré');
    } catch (err: any) {
      const code = err?.response?.data?.error?.code ?? 'ERREUR';
      const msg  = err?.response?.data?.error?.message ?? 'Impossible de télécharger la feuille';
      toast.error(code, msg);
    } finally {
      setTelecharge(false);
    }
  };

  return (
    <>
      <Topbar title="Pièces produites" />
      <div style={{ maxWidth: 1100, margin: '24px auto', padding: '0 16px' }}>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
          <Link to="/operations/production" style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            color: 'var(--tx-2)', fontSize: 13, textDecoration: 'none',
          }}>
            <ArrowLeft size={14} /> Retour aux ordres de fabrication
          </Link>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, marginBottom: 16 }}>
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>
              Pièces produites du lot
            </h1>
            <div style={{ fontSize: 13, color: 'var(--tx-3)', marginTop: 4 }}>
              Traçabilité par pièce · code inscrit sur béton frais · vérifiable via <code style={{ background: 'var(--bg-2)', padding: '1px 4px', borderRadius: 3 }}>/verify/&lt;code&gt;</code>
            </div>
          </div>

          <button
            onClick={handleTelecharger}
            disabled={telecharge || !pieces?.length}
            className="ds-btn"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              padding: '8px 14px', background: '#1E40AF', color: '#fff',
              border: 0, borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600,
              opacity: telecharge || !pieces?.length ? 0.6 : 1,
            }}
          >
            <FileDown size={16} />
            {telecharge ? 'Génération…' : 'Feuille A4 imprimable'}
          </button>
        </div>

        {isLoading && <p style={{ color: 'var(--tx-3)' }}>Chargement…</p>}
        {error && (
          <div style={{
            padding: 12, background: '#FEE2E2', border: '1px solid #FCA5A5',
            borderRadius: 6, color: '#991B1B', marginBottom: 12,
          }}>
            Impossible de charger les pièces pour ce lot.
          </div>
        )}

        {!isLoading && pieces && pieces.length === 0 && (
          <div style={{
            padding: 20, background: 'var(--bg-2)', borderRadius: 8, textAlign: 'center',
            color: 'var(--tx-3)',
          }}>
            <ShieldCheck size={32} style={{ opacity: 0.4 }} />
            <p style={{ marginTop: 8 }}>Aucune pièce n'a été générée pour ce lot.</p>
            <p style={{ fontSize: 12 }}>
              La codification est-elle activée (<code>PIECES_CODIFICATION_ENABLED</code>) ?
              Vérifier aussi que le lot a été créé après activation du secret HMAC.
            </p>
          </div>
        )}

        {!isLoading && pieces && pieces.length > 0 && (
          <>
            {/* Compteurs par statut, cliquables pour filtrer */}
            <div style={{
              display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10,
              marginBottom: 16,
            }}>
              {(Object.keys(STATUT_LABELS) as StatutPiece[]).map((s) => {
                const meta = STATUT_LABELS[s];
                const Icone = meta.icone;
                const actif = filtreStatut === s;
                return (
                  <button
                    key={s}
                    onClick={() => setFiltreStatut(actif ? null : s)}
                    style={{
                      textAlign: 'left', padding: 12,
                      background: actif ? '#EFF6FF' : '#fff',
                      border: actif ? '2px solid #1E40AF' : '1px solid var(--bdr)',
                      borderRadius: 8, cursor: 'pointer',
                    }}
                  >
                    <Icone size={16} style={{ color: 'var(--tx-3)' }} />
                    <div style={{ fontSize: 20, fontWeight: 700, marginTop: 4 }}>
                      {compteurs[s]}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--tx-3)' }}>
                      {meta.label}
                    </div>
                  </button>
                );
              })}
            </div>

            {filtreStatut && (
              <div style={{ marginBottom: 8, fontSize: 13, color: 'var(--tx-3)' }}>
                Filtré sur <strong>{STATUT_LABELS[filtreStatut].label}</strong> ·{' '}
                <button
                  onClick={() => setFiltreStatut(null)}
                  style={{ background: 'none', border: 0, color: '#1E40AF', cursor: 'pointer', padding: 0 }}
                >
                  effacer le filtre
                </button>
              </div>
            )}

            <div style={{
              background: '#fff', border: '1px solid var(--bdr)', borderRadius: 8,
              overflow: 'hidden',
            }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: 'var(--bg-2)', textAlign: 'left' }}>
                    <th style={{ padding: '8px 12px', fontWeight: 600 }}>#</th>
                    <th style={{ padding: '8px 12px', fontWeight: 600 }}>Code affichable (béton)</th>
                    <th style={{ padding: '8px 12px', fontWeight: 600 }}>Code canonique</th>
                    <th style={{ padding: '8px 12px', fontWeight: 600 }}>Statut</th>
                    <th style={{ padding: '8px 12px', fontWeight: 600 }}>Date fab.</th>
                  </tr>
                </thead>
                <tbody>
                  {piecesFiltrees.map((p) => {
                    const meta = STATUT_LABELS[p.statut];
                    return (
                      <tr key={p.id} style={{ borderTop: '1px solid var(--bdr)' }}>
                        <td style={{ padding: '8px 12px', color: 'var(--tx-3)' }}>
                          {p.numeroSequentiel}
                        </td>
                        <td style={{ padding: '8px 12px', fontFamily: 'Consolas, monospace', fontWeight: 600 }}>
                          {p.affichage}
                        </td>
                        <td style={{ padding: '8px 12px', fontFamily: 'Consolas, monospace', fontSize: 12, color: 'var(--tx-3)' }}>
                          <button
                            title="Copier le code"
                            onClick={() => {
                              navigator.clipboard?.writeText(p.codeCanonique);
                              toast.success('Copié', p.codeCanonique);
                            }}
                            style={{ background: 'none', border: 0, color: 'inherit', cursor: 'pointer', padding: 0 }}
                          >
                            {p.codeCanonique}
                          </button>
                        </td>
                        <td style={{ padding: '8px 12px' }}>
                          <span className={`ds-bd ${meta.ton}`} style={{ fontSize: 11 }}>
                            {meta.label}
                          </span>
                        </td>
                        <td style={{ padding: '8px 12px', color: 'var(--tx-3)' }}>
                          {new Date(p.dateFab).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}

      </div>
    </>
  );
}
